/**
 * Suggestions Page - Feature Voting System
 * Users can submit feature ideas, vote on existing ones
 * Monthly top feature gets implemented!
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  CircularProgress,
  Tabs,
  Tab,
  LinearProgress
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BuildIcon from '@mui/icons-material/Build';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useAuth } from '../contexts/AuthContext'; // ✅ FIXED - Correct path with 's'

const Suggestions = () => {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState('votes'); // votes, recent, status
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, planned, in-progress, completed
  const [votingLoading, setVotingLoading] = useState({});

  const [newSuggestion, setNewSuggestion] = useState({
    title: '',
    description: '',
    category: 'feature'
  });

  const categories = [
    { value: 'feature', label: '✨ New Feature', color: '#667eea' },
    { value: 'improvement', label: '🚀 Improvement', color: '#10b981' },
    { value: 'bug', label: '🐛 Bug Fix', color: '#ef4444' },
    { value: 'ui', label: '🎨 UI/UX', color: '#ec4899' },
    { value: 'integration', label: '🔌 Integration', color: '#f59e0b' }
  ];

  const statuses = [
    { value: 'pending', label: 'Under Review', color: '#9ca3af', icon: '👀' },
    { value: 'planned', label: 'Planned', color: '#3b82f6', icon: '📋' },
    { value: 'in-progress', label: 'In Progress', color: '#f59e0b', icon: '⚙️' },
    { value: 'completed', label: 'Completed', color: '#10b981', icon: '✅' }
  ];

  useEffect(() => {
    loadSuggestions();
  }, [sortBy, filterStatus]);

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/suggestions?sort=${sortBy}&status=${filterStatus}`);
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSuggestion = async () => {
    if (!user) {
      alert('Please log in to submit suggestions');
      return;
    }

    if (!newSuggestion.title || !newSuggestion.description) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const response = await fetch('/api/suggestions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newSuggestion)
      });

      if (response.ok) {
        setSubmitDialogOpen(false);
        setNewSuggestion({ title: '', description: '', category: 'feature' });
        loadSuggestions();
      }
    } catch (error) {
      console.error('Failed to submit suggestion:', error);
    }
  };

  const handleVote = async (suggestionId, currentlyVoted) => {
    if (!user) {
      alert('Please log in to vote');
      return;
    }

    setVotingLoading(prev => ({ ...prev, [suggestionId]: true }));

    try {
      const response = await fetch(`/api/suggestions/${suggestionId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ vote: !currentlyVoted })
      });

      if (response.ok) {
        loadSuggestions();
      }
    } catch (error) {
      console.error('Failed to vote:', error);
    } finally {
      setVotingLoading(prev => ({ ...prev, [suggestionId]: false }));
    }
  };

  const getCategoryColor = (category) => {
    return categories.find(c => c.value === category)?.color || '#667eea';
  };

  const getStatusInfo = (status) => {
    return statuses.find(s => s.value === status) || statuses[0];
  };

  const sortedSuggestions = [...suggestions].sort((a, b) => {
    if (sortBy === 'votes') return b.votes - a.votes;
    if (sortBy === 'recent') return new Date(b.created_at) - new Date(a.created_at);
    return 0;
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f9fafb', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            💡 Feature Suggestions
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Help us build what YOU want! Vote on ideas and submit your own.
          </Typography>

          <Alert 
            severity="info"
            icon={<TrendingUpIcon />}
            sx={{ 
              maxWidth: 700, 
              mx: 'auto', 
              mb: 4,
              bgcolor: '#dbeafe',
              color: '#1e40af'
            }}
          >
            🔥 <strong>Top voted feature each month gets implemented!</strong> Your vote matters.
          </Alert>

          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setSubmitDialogOpen(true)}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600
            }}
          >
            Submit Your Idea
          </Button>
        </Box>

        {/* Filters & Sort */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Filter by Status
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label="All"
                  onClick={() => setFilterStatus('all')}
                  color={filterStatus === 'all' ? 'primary' : 'default'}
                  sx={{ fontWeight: 600 }}
                />
                {statuses.map(status => (
                  <Chip
                    key={status.value}
                    label={`${status.icon} ${status.label}`}
                    onClick={() => setFilterStatus(status.value)}
                    color={filterStatus === status.value ? 'primary' : 'default'}
                    sx={{ 
                      fontWeight: 600,
                      bgcolor: filterStatus === status.value ? status.color : undefined,
                      color: filterStatus === status.value ? 'white' : undefined
                    }}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Sort By
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={sortBy === 'votes' ? 'contained' : 'outlined'}
                  startIcon={<TrendingUpIcon />}
                  onClick={() => setSortBy('votes')}
                >
                  Most Voted
                </Button>
                <Button
                  variant={sortBy === 'recent' ? 'contained' : 'outlined'}
                  startIcon={<AccessTimeIcon />}
                  onClick={() => setSortBy('recent')}
                >
                  Recent
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Suggestions List */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : sortedSuggestions.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <LightbulbIcon sx={{ fontSize: 80, color: '#9ca3af', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No suggestions yet. Be the first to submit an idea!
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {sortedSuggestions.map((suggestion) => {
              const statusInfo = getStatusInfo(suggestion.status);
              const categoryInfo = categories.find(c => c.value === suggestion.category);
              const userVoted = suggestion.user_voted; // From API

              return (
                <Grid item xs={12} key={suggestion.id}>
                  <Card 
                    sx={{ 
                      transition: 'all 0.3s',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={2}>
                        {/* Vote Button */}
                        <Grid item xs="auto">
                          <Box sx={{ textAlign: 'center' }}>
                            <IconButton
                              onClick={() => handleVote(suggestion.id, userVoted)}
                              disabled={votingLoading[suggestion.id]}
                              sx={{
                                color: userVoted ? '#667eea' : '#9ca3af',
                                '&:hover': {
                                  bgcolor: '#667eea15'
                                }
                              }}
                            >
                              {userVoted ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                            </IconButton>
                            <Typography 
                              variant="h6" 
                              fontWeight="bold"
                              sx={{ color: userVoted ? '#667eea' : '#6b7280' }}
                            >
                              {suggestion.votes}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Content */}
                        <Grid item xs>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip
                              label={categoryInfo?.label || suggestion.category}
                              size="small"
                              sx={{
                                bgcolor: `${getCategoryColor(suggestion.category)}15`,
                                color: getCategoryColor(suggestion.category),
                                fontWeight: 600
                              }}
                            />
                            <Chip
                              label={`${statusInfo.icon} ${statusInfo.label}`}
                              size="small"
                              sx={{
                                bgcolor: `${statusInfo.color}15`,
                                color: statusInfo.color,
                                fontWeight: 600
                              }}
                            />
                            {suggestion.is_top_voted && (
                              <Chip
                                label="🔥 Top Voted"
                                size="small"
                                sx={{
                                  bgcolor: '#fef3c7',
                                  color: '#f59e0b',
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </Box>

                          <Typography variant="h6" fontWeight="bold" gutterBottom>
                            {suggestion.title}
                          </Typography>

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {suggestion.description}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            Submitted by {suggestion.author_name || 'Anonymous'} • {new Date(suggestion.created_at).toLocaleDateString()}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* Submit Dialog */}
      <Dialog 
        open={submitDialogOpen} 
        onClose={() => setSubmitDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LightbulbIcon sx={{ color: '#f59e0b' }} />
            <span>Submit Your Idea</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Title"
              value={newSuggestion.title}
              onChange={(e) => setNewSuggestion({ ...newSuggestion, title: e.target.value })}
              placeholder="e.g., Add dark mode theme option"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              select
              label="Category"
              value={newSuggestion.category}
              onChange={(e) => setNewSuggestion({ ...newSuggestion, category: e.target.value })}
              sx={{ mb: 2 }}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Description"
              value={newSuggestion.description}
              onChange={(e) => setNewSuggestion({ ...newSuggestion, description: e.target.value })}
              multiline
              rows={4}
              placeholder="Describe your feature idea in detail. What problem does it solve? How would it work?"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setSubmitDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitSuggestion}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}
          >
            Submit Idea
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Suggestions;